<template>
  <v-list-item
    :key="contact.id"
    class="contact-list-item"
    lines="two"
    @click="navigateToContact"
  >
    <template #prepend>
      <v-avatar
        :color="getContactColor(contact.type)"
        size="42"
        class="contact-avatar"
      >
        <v-icon :icon="getContactIcon(contact.type)" color="white" size="22" />
      </v-avatar>
    </template>

    <template #default>
      <!-- Primary line: name + location + tree affordances -->
      <v-list-item-title class="d-flex align-center flex-wrap contact-title">
        <span class="text-subtitle-1 font-weight-medium">
          {{ contact.display_name || contact.name || "Naamloos contact" }}
        </span>

        <!-- Location disambiguator (e.g. city) -->
        <span v-if="locationLabel" class="location-label text-medium-emphasis">
          <v-icon size="13" class="mr-1">mdi-map-marker-outline</v-icon
          >{{ locationLabel }}
        </span>

        <!-- Slot for tree affordances (child count, etc.) -->
        <slot name="after-name" />
      </v-list-item-title>

      <!-- Secondary line: quiet metadata, one row, no competing colours -->
      <v-list-item-subtitle
        class="d-flex align-center flex-wrap contact-meta text-medium-emphasis"
      >
        <!-- Type (avatar already carries the colour, so this stays muted text) -->
        <span class="meta-item">{{ getContactTypeLabel(contact.type) }}</span>

        <!-- Parent breadcrumb for departments / individuals -->
        <span v-if="hierarchyText" class="meta-item">
          <v-icon size="12" class="mr-1">mdi-subdirectory-arrow-right</v-icon
          >{{ hierarchyText }}
        </span>

        <!-- Primary phone -->
        <span
          v-if="primaryPhone"
          class="meta-item"
          :title="primaryPhone.formatted_display || primaryPhone.phone_number"
        >
          <v-icon
            :icon="getPhoneIcon(primaryPhone.phone_type)"
            size="13"
            class="mr-1"
          />{{ primaryPhone.phone_number }}
        </span>

        <!-- Primary email -->
        <span v-if="primaryEmail" class="meta-item" :title="primaryEmail">
          <v-icon size="13" class="mr-1">mdi-email-outline</v-icon
          >{{ truncateText(primaryEmail, 28) }}
        </span>

        <!-- Categories: the one place colour is meaningful -->
        <v-chip
          v-for="category in contactCategories"
          :key="category.id"
          :color="category.color || 'grey'"
          size="x-small"
          variant="tonal"
          class="category-chip"
        >
          <v-icon :icon="category.icon || 'mdi-tag'" start size="11" />
          {{ category.name }}
        </v-chip>
      </v-list-item-subtitle>
    </template>

    <template #append>
      <div class="d-flex align-center contact-actions" @click.stop>
        <!-- One-tap call / mail, only when the data exists -->
        <v-btn
          v-if="primaryPhone"
          icon="mdi-phone"
          variant="text"
          size="small"
          color="medium-emphasis"
          :href="`tel:${primaryPhone.phone_number}`"
          title="Bellen"
          @click.stop.prevent
        />
        <v-btn
          v-if="primaryEmail"
          icon="mdi-email"
          variant="text"
          size="small"
          color="medium-emphasis"
          :href="`mailto:${primaryEmail}`"
          title="E-mail sturen"
          @click.stop.prevent
        />

        <!-- Everything else folds into an overflow menu to keep rows calm -->
        <v-menu location="bottom end">
          <template #activator="{ props: menuProps }">
            <v-btn
              icon="mdi-dots-vertical"
              variant="text"
              size="small"
              color="medium-emphasis"
              title="Meer acties"
              v-bind="menuProps"
              @click.stop
            />
          </template>
          <v-list density="compact">
            <v-list-item prepend-icon="mdi-pencil" @click="editContact">
              <v-list-item-title>Bewerken</v-list-item-title>
            </v-list-item>
            <v-list-item
              prepend-icon="mdi-delete"
              base-color="error"
              @click="showDeleteDialog = true"
            >
              <v-list-item-title>Verwijderen</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- Delete confirmation dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" @click.stop>
        <v-card>
          <v-card-title class="text-h5">
            <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
            Contact verwijderen
          </v-card-title>

          <v-card-text>
            <div class="mb-4">
              Weet je zeker dat je
              <strong>{{ contact.display_name || contact.name }}</strong> wilt
              verwijderen?
            </div>

            <div v-if="hasRelatedData" class="mb-4">
              <v-alert type="warning" variant="tonal">
                <v-icon start>mdi-information</v-icon>
                Dit contact heeft gerelateerde gegevens:
                <ul class="mt-2">
                  <li v-if="contact.emails && contact.emails.length > 0">
                    {{ contact.emails.length }} e-mailadres(sen)
                  </li>
                  <li
                    v-if="
                      contact.phone_numbers && contact.phone_numbers.length > 0
                    "
                  >
                    {{ contact.phone_numbers.length }} telefoonnummer(s)
                  </li>
                  <li v-if="contact.addresses && contact.addresses.length > 0">
                    {{ contact.addresses.length }} adres(sen)
                  </li>
                  <li v-if="contact.notes && contact.notes.length > 0">
                    {{ contact.notes.length }} notitie(s)
                  </li>
                  <li v-if="contact.children && contact.children.length > 0">
                    {{ contact.children.length }} onderliggende contact(en)
                  </li>
                </ul>
              </v-alert>
            </div>

            <div class="text-caption text-medium-emphasis">
              Deze actie kan niet ongedaan worden gemaakt.
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="showDeleteDialog = false">
              Annuleren
            </v-btn>
            <v-btn
              color="error"
              variant="elevated"
              :loading="deleteLoading"
              @click="confirmDelete"
            >
              <v-icon start>mdi-delete</v-icon>
              Verwijderen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </v-list-item>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useContactsStoreRefactor } from "@/stores/contactsStoreRefactor";
import { useFormsStore } from "@/stores/formsStore";
import { useAlertStore } from "@/stores/alertStore";

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const router = useRouter();
const contactsStore = useContactsStoreRefactor();
const formsStore = useFormsStore();
const alertStore = useAlertStore();

// Reactive data
const showDeleteDialog = ref(false);
const deleteLoading = ref(false);

// Computed properties
const primaryEmail = computed(() => {
  if (props.contact.primary_email_address) {
    return props.contact.primary_email_address;
  }

  if (props.contact.emails && props.contact.emails.length > 0) {
    const primary = props.contact.emails.find((email) => email.primary);
    return primary ? primary.email : props.contact.emails[0].email;
  }

  return null;
});

const primaryPhone = computed(() => {
  if (props.contact.phone_numbers && props.contact.phone_numbers.length > 0) {
    const primary = props.contact.phone_numbers.find((phone) => phone.primary);
    return primary || props.contact.phone_numbers[0];
  }

  return null;
});

const contactCategories = computed(() => {
  return props.contact.contact_categories || [];
});

// Concise location label (prefers city) derived from the primary location's
// formatted address, used to disambiguate same-named branches in the list.
const locationLabel = computed(() => {
  const raw = props.contact.primary_location_address;
  if (!raw || typeof raw !== "string") return null;

  const segments = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (segments.length === 0) return null;

  // Drop a trailing country segment when there's something more specific.
  const countries = ["netherlands", "nederland"];
  let candidates = segments;
  if (
    candidates.length > 1 &&
    countries.includes(candidates[candidates.length - 1].toLowerCase())
  ) {
    candidates = candidates.slice(0, -1);
  }

  // Last remaining segment is typically "1234 AB City" or "City".
  let label = candidates[candidates.length - 1];
  // Strip a leading Dutch postal code if present.
  label = label.replace(/^\d{4}\s?[A-Za-z]{2}\s+/, "").trim();

  if (!label) return null;
  return label.length > 28 ? label.substring(0, 28) + "…" : label;
});

const hierarchyText = computed(() => {
  if (props.contact.type === "individual" && props.contact.parent_name) {
    return props.contact.parent_name;
  }

  if (props.contact.type === "department" && props.contact.parent_name) {
    return props.contact.parent_name;
  }

  return null;
});

const hasRelatedData = computed(() => {
  return (
    (props.contact.emails && props.contact.emails.length > 0) ||
    (props.contact.phone_numbers && props.contact.phone_numbers.length > 0) ||
    (props.contact.addresses && props.contact.addresses.length > 0) ||
    (props.contact.notes && props.contact.notes.length > 0) ||
    (props.contact.children && props.contact.children.length > 0)
  );
});

// Methods
const navigateToContact = async () => {
  try {
    // Prevent navigation if already on the same contact
    if (router.currentRoute.value.params.id === String(props.contact.id)) {
      return;
    }
    await router.push(`/contacts/${props.contact.id}`);
  } catch (error) {
    console.error("Navigation error:", error);
    setTimeout(() => {
      try {
        router.push(`/contacts/${props.contact.id}`);
      } catch (fallbackError) {
        console.error("Fallback navigation failed:", fallbackError);
      }
    }, 100);
  }
};

const editContact = (event) => {
  try {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    formsStore.edit({
      type: "contact",
      item: { ...props.contact },
    });
  } catch (error) {
    console.error("Error opening edit form:", error);
  }
};

const confirmDelete = async () => {
  try {
    deleteLoading.value = true;

    await contactsStore.destroy(props.contact.id);

    showDeleteDialog.value = false;
    alertStore.showSuccess(
      `Contact "${props.contact.display_name || props.contact.name}" is succesvol verwijderd`,
    );

    if (router.currentRoute.value.params.id === String(props.contact.id)) {
      router.push("/contacts");
    }
  } catch (error) {
    console.error("Error deleting contact:", error);
    alertStore.showError(
      error.message ||
        "Er is een fout opgetreden bij het verwijderen van het contact",
    );
  } finally {
    deleteLoading.value = false;
  }
};

// Helper methods
const getContactIcon = (type) => {
  return contactsStore.icons[type]?.icon || "mdi-help-circle";
};

const getContactColor = (type) => {
  return contactsStore.icons[type]?.color || "grey";
};

const getContactTypeLabel = (type) => {
  const labels = {
    organization: "Bedrijf",
    department: "Afdeling",
    individual: "Persoon",
  };
  return labels[type] || "Onbekend";
};

const getPhoneIcon = (phoneType) => {
  const config = contactsStore.getPhoneTypeConfig(phoneType);
  return config.icon;
};

const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "…";
};
</script>

<style scoped>
.contact-list-item {
  transition: background-color 0.2s ease;
}

.contact-list-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.contact-avatar {
  flex-shrink: 0;
}

.contact-title {
  gap: 8px;
  row-gap: 2px;
  white-space: normal;
}

.location-label {
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
}

.contact-meta {
  gap: 4px 14px;
  margin-top: 2px;
  white-space: normal;
}

.contact-meta .meta-item {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  line-height: 1.4;
}

.category-chip {
  font-weight: 500;
}

/* Actions stay quiet until the row is hovered (still tappable on touch) */
.contact-actions {
  opacity: 0.65;
  transition: opacity 0.2s ease;
}

.contact-list-item:hover .contact-actions {
  opacity: 1;
}

@media (hover: none) {
  .contact-actions {
    opacity: 1;
  }
}
</style>
